/**
 * "Combined from" — the provenance block for a Combine & Filter result (epic
 * 2026-09-22, sub-task 05).
 *
 * Rendered by `FullHistory` IN PLACE of the single-parent chain: a combined
 * import has no `parent_import_id` (`ImportLineage.chain` is always empty for
 * one), and `ImportLineage.sources` carries the lists it was built from
 * instead — see that schema's own docstring ("a client renders whichever of
 * the two is non-empty").
 *
 * Source rows show the list's type, title, who ran it, its contact count, and
 * a link to its own detail page. A source that was itself built from other
 * lists (`has_history`) gets a chevron that unfolds ITS lineage underneath,
 * recursively — see `LineageExpander`. Nested blocks render `compact`: no
 * recipe summary, since the recipe belongs to the import whose page this is.
 *
 * The recipe summary below it — filters (with their operator), "only people
 * in every list", exclusions, and the "already in Thrive" removal — comes
 * from `ImportDetail.combine_recipe` (`StoredCombineRecipe`), which
 * `FullHistory` receives as a prop and passes straight through (the lineage
 * endpoint itself does not carry the recipe). A recipe stored before
 * `remove_in_thrive` existed was not filtered by it; the server reports it
 * as `off` (bug hunt 2026-09-23, B6), so it gets no line. Likewise a recipe
 * stored before task 2026-09-24-combine-more-filters has none of that task's
 * keys (the server reads each as `null`, filter off), so it renders the same
 * lines it always did.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type {
  CombineFilter,
  CombineFilterClause,
  CombineFilterOp,
  ImportLineage,
  StoredCombineRecipe,
} from "@nu-assist/contracts";

import { formatContactCount } from "../../../features/imports/selection/importLabels";
import { TAG_DEF_BY_VALUE, deriveTypeTag } from "../imports/tagDefs";
import { displayTitle } from "./importTitle";
import { type LineageExpandContext, LineageExpander } from "./LineageExpander";

export interface CombinedFromBlockProps {
  /** `ImportLineage.sources`, already narrowed to non-empty by the caller. */
  sources: NonNullable<ImportLineage["sources"]>;
  /** `ImportDetail.combine_recipe` — `null` renders the source list with no
   *  recipe summary beneath it (the detail hasn't resolved yet, or somehow
   *  carries none). */
  recipe: StoredCombineRecipe | null;
  /** Nested inside an expanded row: never renders a recipe summary. */
  compact?: boolean;
  /** Where this block sits in the expansion tree. Absent, source rows get no
   *  chevron (links only). */
  expand?: LineageExpandContext;
}

/** The `CombineFilter` fields that are text clauses — the only keys the
 *  two maps below cover (the others are not clauses). */
type TextClauseField = "title" | "company" | "location" | "past_company" | "past_title" | "keyword";

/** In line order. */
const FILTER_FIELD_LABEL: Record<TextClauseField, string> = {
  title: "Title",
  company: "Company",
  location: "Location",
  past_company: "Past company",
  past_title: "Past title",
  keyword: "Summary or headline",
};

/** The operator a clause with `op: null` ran with — the server's own
 *  field default (the shared `filters_from_recipe`). */
const DEFAULT_OP: Record<TextClauseField, CombineFilterOp> = {
  title: "contains",
  company: "is",
  location: "is",
  past_company: "is",
  past_title: "contains",
  keyword: "contains",
};

const DEGREE_LABEL: Record<number, string> = { 1: "1st", 2: "2nd", 3: "3rd" };

const HAS_EMAIL_LINE: Record<NonNullable<CombineFilter["has_email"]>, string> = {
  any: "Has an email",
  personal: "Has a personal email",
  work: "Has a work email",
  none: "Has no email",
};

/** "a, b or c". */
function orList(items: string[]): string {
  return items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
}

/** "Sep 24, 2026" for the stored ISO day, read as a calendar day (UTC), so
 *  no time zone can move it. */
function dayLabel(isoDay: string): string {
  return new Date(`${isoDay}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function tenureText(
  clause: NonNullable<StoredCombineRecipe["filters"]>["months_in_role"],
  referenceDate: string | null | undefined
): string | null {
  const min = clause?.min_months ?? null;
  const max = clause?.max_months ?? null;
  const range =
    min !== null && max !== null
      ? `${min}–${max} months`
      : min !== null
        ? `at least ${min} months`
        : max !== null
          ? `at most ${max} months`
          : null;
  if (range === null) return null;
  const asOf = referenceDate ? ` (as of ${dayLabel(referenceDate)})` : "";
  return `In current role ${range}${asOf}`;
}

const VERB: Record<CombineFilterClause["mode"], Record<CombineFilterOp, string>> = {
  include: { contains: "contains", is: "is" },
  exclude: { contains: "does not contain", is: "is not" },
};

function clauseText(field: TextClauseField, clause: CombineFilterClause): string {
  const verb = VERB[clause.mode][clause.op ?? DEFAULT_OP[field]];
  return `${FILTER_FIELD_LABEL[field]} ${verb} ${clause.values.join(", ")}`;
}

const REMOVE_IN_THRIVE_LINE: Record<StoredCombineRecipe["remove_in_thrive"], string | null> = {
  off: null,
  search: "Removed people already in this search",
  any: "Removed people already in Thrive",
};

/**
 * Plain-language recipe lines: one per active filter clause ("Title
 * contains VP", "Company is not Acme", "Past company is Acme", "Summary or
 * headline contains fintech"), then time in role ("In current role 12–36
 * months (as of Sep 24, 2026)"), connection degree ("Connection 1st or
 * 2nd"), email ("Has a work email") and Thrive candidacy stage ("Candidacy
 * stage Client Interview or Offer", 05); then "Only people in every list"
 * when `require_all_sources` is set, or "In at least 3 of 5 lists" for a
 * smaller `min_sources`; then how many the recruiter unticked before
 * generating, then the "already in Thrive" removal when it was on. Empty
 * when the recipe applied none of them — a bare "combined from N lists" is
 * still an honest summary of that recipe.
 */
export function describeCombineRecipe(recipe: StoredCombineRecipe): string[] {
  const lines: string[] = [];
  const filters = recipe.filters;
  if (filters) {
    for (const field of Object.keys(FILTER_FIELD_LABEL) as TextClauseField[]) {
      const clause = filters[field];
      if (clause && clause.values.length > 0) {
        lines.push(clauseText(field, clause));
      }
    }
    const tenure = filters.months_in_role
      ? tenureText(filters.months_in_role, recipe.reference_date)
      : null;
    if (tenure) lines.push(tenure);
    const degrees = [...(filters.connection_degrees ?? [])]
      .sort()
      .map((d) => DEGREE_LABEL[d])
      .filter(Boolean);
    if (degrees.length > 0) lines.push(`Connection ${orList(degrees)}`);
    if (filters.has_email) lines.push(HAS_EMAIL_LINE[filters.has_email]);
    if (filters.candidacy_stages?.length) {
      lines.push(`Candidacy stage ${orList(filters.candidacy_stages)}`);
    }
  }
  const sourceCount = new Set(recipe.source_import_ids).size;
  if (recipe.require_all_sources) lines.push("Only people in every list");
  else if (recipe.min_sources != null && recipe.min_sources < sourceCount) {
    lines.push(`In at least ${recipe.min_sources} of ${sourceCount} lists`);
  }
  const excludedCount = recipe.excluded_contact_ids?.length ?? 0;
  if (excludedCount > 0) {
    lines.push(`${excludedCount} excluded before generating`);
  }
  const thriveLine = REMOVE_IN_THRIVE_LINE[recipe.remove_in_thrive];
  if (thriveLine) lines.push(thriveLine);
  return lines;
}

/** A source row's type def — the SAME table `FullHistory`'s own chain segments
 *  and the list pill read, so a source cannot disagree with the badge on the
 *  page it links to. */
function typeDefOf(tags: string[] | undefined, importFormat: string | undefined) {
  const tag = deriveTypeTag(
    (tags ?? []) as Parameters<typeof deriveTypeTag>[0],
    importFormat as Parameters<typeof deriveTypeTag>[1]
  );
  return tag ? TAG_DEF_BY_VALUE[tag] : undefined;
}

export function CombinedFromBlock({ sources, recipe, compact = false, expand }: CombinedFromBlockProps) {
  const recipeLines = recipe && !compact ? describeCombineRecipe(recipe) : [];

  return (
    <div data-testid="combined-from-block" className="flex flex-col gap-2">
      <div className="text-caption text-text-secondary">
        Combined from {sources.length} {sources.length === 1 ? "list" : "lists"}
      </div>
      <ol className="flex flex-col gap-1.5">
        {sources.map((entry, i) => {
          const def = typeDefOf(entry.tags, entry.import_format);
          const label = def?.label ?? "Import";
          const row = (toggle: ReactNode) => (
            <div className="flex items-start gap-1.5">
              {expand && (toggle ?? <span aria-hidden="true" className="w-4 shrink-0" />)}
              <Link
                data-testid="combined-from-source-link"
                to={`/research/imports/${entry.id}`}
                className={`inline-flex w-fit shrink-0 items-center rounded-pill px-2 py-0.5 text-caption hover:underline ${
                  def?.pillClass ?? "bg-surface text-text-secondary"
                }`}
              >
                {label}
              </Link>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span
                  data-testid="combined-from-source-title"
                  className="truncate text-caption text-text"
                  title={entry.filename}
                >
                  {displayTitle(entry.filename)}
                </span>
                <span className="flex flex-wrap gap-x-1 text-caption text-text-muted">
                  <span data-testid="combined-from-source-owner">{entry.run_by}</span>
                  <span aria-hidden="true">·</span>
                  <span data-testid="combined-from-source-count" className="whitespace-nowrap">
                    {formatContactCount(entry.total_contacts ?? 0)}
                  </span>
                </span>
              </span>
            </div>
          );
          return (
            <li key={`${entry.id}-${i}`}>
              {expand ? (
                <LineageExpander entry={entry} ctx={expand}>
                  {row}
                </LineageExpander>
              ) : (
                row(null)
              )}
            </li>
          );
        })}
      </ol>
      {recipeLines.length > 0 && (
        <ul
          data-testid="combined-from-recipe"
          className="flex flex-col gap-0.5 border-t border-border pt-1.5"
        >
          {recipeLines.map((line) => (
            <li key={line} className="text-caption text-text-secondary">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
